import qs from 'qs'
import Vue from 'vue'
import { api } from '@/services'
import {
  fetchBranchesAsAdmin,
  fetchBranch,
  deleteBranch,
  create,
  update,
  fetchOwnBranch,
  createOwnBranch,
  updateOwnBranch,
  deleteOwnBranch,
} from '@/services/branch/branchService'

const state = {
  branches: [],
  branch: {},
  // Kept apart from `branch`: that one is shared with the appointment flow, where the legacy
  // fetchBranch action commits an array into it.
  ownBranch: {},
  isSavingBranch: false,
}

const getters = {
  branches: (state) => state.branches,
  branch: (state) => state.branch,
  ownBranch: (state) => state.ownBranch,
  isSavingBranch: (state) => state.isSavingBranch,
}

const actions = {
  async fetchBranches({ commit, dispatch }, realEstateId) {
    try {
      let url = '/web/branch/list'
      if (realEstateId) {
        url += `?realEstateId=${realEstateId}`
      }
      const { data } = await api.get(url)
      commit('setBranches', data)
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    }
  },

  async fetchBranch({ commit, dispatch }, id) {
    commit('setIsLoading', true)

    try {
      const { data } = await api.get(`/web/branch/list`)
      const branch = await data.filter((branch) => branch.id === id)
      commit('setBranch', branch)
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsLoading', false)
    }
  },

  async fetchBranchesAsAdmin({ commit, dispatch, rootState }) {
    const defaultParams = {
      size: rootState.paginate.itemsPerPage,
      page: rootState.paginate.currentPage - 1,
    }
    const params = { ...defaultParams, ...rootState.filters.filters }
    fetchBranchesAsAdmin(params)
    .then(({data}) => {
      const { result, total } = data
      commit('setBranches', result)
      commit('setTotalPages', total)
    })
    .catch(({data}) => {
      dispatch('showErrorMessage', data.message)
    })
  },

  async fetchBranchAsAdmin({ commit, dispatch }, id) {
    commit('setIsLoading', true)
    try {
      const { data }  = await fetchBranch(id)
      commit('setBranch', data)
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsLoading', false)
    }
  },

  async deleteBranch({ commit, dispatch }, id) {
    commit('setIsLoading', true)
    try {
      await deleteBranch(id)
      dispatch('showSuccessMessage', Vue.i18n.translate('customer.branches.messages.successDelete'))
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsLoading', false)
    }
  },

  async saveBranchAsAdmin({ commit, dispatch }, body) {
    commit('setIsSavingBranch', true)

    try {
      if (body.branch_id) await update(body)
        .then(() => {
          commit('closeAllModals')
          dispatch('fetchBranchesAsAdmin')
          dispatch('showSuccessMessage', Vue.i18n.translate('customer.branches.messages.successUpdate'))
        })
      else await create(body)
        .then(() => {
          commit('closeAllModals')
          dispatch('fetchBranchesAsAdmin')
          dispatch('showSuccessMessage', Vue.i18n.translate('customer.branches.messages.successCreation'))
        })
    } catch ({ data }) {
      data?.errors && data?.errors?.length >= 1
        ? dispatch('showErrorMessage', data.erros[0].message)
        : dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsSavingBranch', false)
    }
  },

  async fetchOwnBranch({ commit, dispatch }, id) {
    commit('setIsLoading', true)
    try {
      const { data } = await fetchOwnBranch(id)
      commit('setOwnBranch', data)
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsLoading', false)
    }
  },

  async saveOwnBranch({ commit, dispatch }, body) {
    commit('setIsSavingBranch', true)

    try {
      const isUpdate = Boolean(body.branch_id)
      await (isUpdate ? updateOwnBranch(body) : createOwnBranch(body))
      commit('closeAllModals')
      dispatch('fetchBranches')
      dispatch(
        'showSuccessMessage',
        Vue.i18n.translate(
          isUpdate
            ? 'customer.branches.messages.successUpdate'
            : 'customer.branches.messages.successCreation'
        )
      )
    } catch ({ data }) {
      data?.errors && data?.errors?.length >= 1
        ? dispatch('showErrorMessage', data.errors[0].message)
        : dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsSavingBranch', false)
    }
  },

  async deleteOwnBranch({ commit, dispatch }, id) {
    commit('setIsLoading', true)
    try {
      await deleteOwnBranch(id)
      dispatch('fetchBranches')
      dispatch('showSuccessMessage', Vue.i18n.translate('customer.branches.messages.successDelete'))
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsLoading', false)
    }
  },

  async fetchBranchBySearchText({ commit, dispatch }, params) {
    const parsedParams = qs.stringify(params, { arrayFormat: 'repeat' })

    try {
      const { data } = await api.get('admin/dashboard/branchesBySearchText?' + parsedParams)
      commit('setBranches', data)
    } catch ({ data }) {
      data?.errors && data?.errors?.length >= 1
        ? dispatch('showErrorMessage', data.erros[0].message)
        : dispatch('showErrorMessage', data.message)
    }
  },
}

const mutations = {
  setBranches: (state, status) => {
    state.branches = status
  },

  setBranch: (state, branch) => {
    state.branch = branch
  },

  setOwnBranch: (state, branch) => {
    state.ownBranch = branch
  },

  setIsSavingBranch: (state, status) => {
    state.isSavingBranch = status
  }
}

export default {
  state,
  mutations,
  actions,
  getters,
}
